<div class="subscription-page">
  <div class="page-header">
    <app-back-button
      parentRoute="/admin/process-management/subscriptions"
      [title]="pageHeading().title"
      [subtitle]="pageHeading().subtitle"
    ></app-back-button>
    <div class="header-actions">
      <app-secondary-button
        title="Edit"
        icon="pi pi-pencil"
        size="small"
        [disable]="!source()"
        (action)="edit()"
      ></app-secondary-button>
      <app-secondary-button
        title="Create copy"
        icon="pi pi-copy"
        size="small"
        [disable]="!source()"
        (action)="copy()"
      ></app-secondary-button>
    </div>
  </div>

  @if (errorMessage()) {
    <div class="error-message">{{ errorMessage() | translate }}</div>
  }

  @if (subscription(); as selected) {
    <header class="subscription-heading">
      <div>
        <h2>{{ selected.name | translate }}</h2>
        <div class="subscription-meta">
          @if (selected.processUrl) {
            <a [routerLink]="selected.processUrl">{{ selected.processLabel | translate }}</a>
          } @else {
            <span>{{ selected.processLabel | translate }}</span>
          }
          <app-copyable-identifier [value]="selected.processKey"></app-copyable-identifier>
          <p-tag [value]="selected.statusLabel | translate" [severity]="selected.statusSeverity"></p-tag>
        </div>
        <p class="subscription-schedule">
          <i class="pi pi-clock" aria-hidden="true"></i>
          <strong>{{ selected.cronLabel }}</strong>
          <span>{{ selected.timeZoneLabel }}</span>
        </p>
        <dl class="subscription-times">
          <div>
            <dt>{{ 'Next run' | translate }}</dt>
            <dd>
              @if (selected.nextFireAt) {
                {{ selected.nextFireAt | dateFormatLang:translate.currentLang:true }}
              } @else {
                —
              }
            </dd>
          </div>
          <div>
            <dt>{{ 'Last run' | translate }}</dt>
            <dd>
              @if (selected.lastTriggeredAt) {
                {{ selected.lastTriggeredAt | dateFormatLang:translate.currentLang:true }}
              } @else {
                —
              }
            </dd>
          </div>
          <div>
            <dt>{{ 'When the run is interrupted' | translate }}</dt>
            <dd>{{ selected.interruptionLabel | translate }}</dd>
          </div>
          @if (selected.resumeAttemptLimit !== null) {
            <div>
              <dt>{{ 'Resume attempts' | translate }}</dt>
              <dd>{{ selected.resumeAttemptLimit }}</dd>
            </div>
          }
          <div>
            <dt>{{ 'Pause after consecutive failures' | translate }}</dt>
            <dd>{{ selected.pauseAfterConsecutiveFailures }}</dd>
          </div>
          <div>
            <dt>{{ 'Last assignment' | translate }}</dt>
            <dd>
              <app-copyable-identifier
                [value]="selected.lastAssignmentId || ''"
                [link]="selected.lastAssignmentUrl"
              ></app-copyable-identifier>
            </dd>
          </div>
        </dl>
        @if (selected.pauseReasonLabel) {
          <p class="subscription-pause">{{ selected.pauseReasonLabel | translate }}</p>
        }
      </div>
    </header>

    @if (selected.safeInputJson) {
      <section class="subscription-section">
        <h3>{{ 'Process parameters' | translate }}</h3>
        <pre><code>{{ selected.safeInputJson | prettyJson }}</code></pre>
      </section>
    }

    <section class="subscription-section assignments-panel orch-table-panel">
      <app-orchestration-table-heading heading="Assignments" [(query)]="assignmentSearch">
        <p class="section-hint">{{ 'Assignments started by this subscription' | translate }}</p>
        <div tableActions class="table-actions">
          <app-primary-button
            title="Run now"
            icon="pi pi-play"
            size="small"
            [disable]="!subscription() || running()"
            [loading]="running()"
            (action)="runNow()"
          ></app-primary-button>
          <app-secondary-button
            title="Refresh data"
            icon="refresh"
            size="small"
            [disable]="loading()"
            [loading]="loading()"
            (action)="reload()"
          ></app-secondary-button>
        </div>
      </app-orchestration-table-heading>
      <div class="assignment-toolbar">
        <div class="assignment-toolbar__group">
          <app-assignment-result-filter
            [selected]="resultFilter()"
            (selectedChange)="onResultFilterChange($event)"
          ></app-assignment-result-filter>
        </div>
        <div class="assignment-toolbar__group state-filters">
          @for (chip of stateChips(); track chip.id) {
            <button
              type="button"
              class="filter-chip"
              [class.active]="chip.selected"
              (click)="selectStateFilter(chip.id)">
              <span>{{ chip.label | translate }}</span>
              <strong>{{ chip.count }}</strong>
            </button>
          }
        </div>
        <div class="assignment-toolbar__group">
          <app-date-picker
            [ngModel]="createdRange()"
            (ngModelChange)="onCreatedRangeChange($event)"
            selectionMode="range"
            [showClear]="true"
            [placeholder]="'Created' | translate"
          ></app-date-picker>
        </div>
      </div>
      <app-data-table
        [columns]="assignmentColumns()"
        [data]="visibleAssignments()"
        [config]="assignmentTableConfig"
        [loading]="loading()"
        (onRowClick)="openAssignment($event)"
      ></app-data-table>
    </section>
  } @else if (loading()) {
    <app-loading [loading]="true"></app-loading>
  } @else if (notFound()) {
    <div class="empty-state">{{ 'Subscription not found' | translate }}</div>
  }
</div>

<app-subscription-form-dialog (saved)="onSubscriptionSaved($event)"></app-subscription-form-dialog>

<ng-template #assignmentNameTemplate let-row>
  <div class="name-cell">
    <strong>{{ row.name | translate }}</strong>
    @if (row.error) {
      <small>{{ row.error }}</small>
    }
  </div>
</ng-template>

<ng-template #processTemplate let-row>
  <span>{{ row.processLabel | translate }}</span>
</ng-template>

<ng-template #assignmentStateTemplate let-row>
  <div class="status-cell">
    <p-tag [value]="row.stateLabel | translate" [severity]="row.stateSeverity"></p-tag>
    @if (row.statusAt) {
      <small>{{ row.statusAt | dateFormatLang:translate.currentLang:true }}</small>
    }
  </div>
</ng-template>

<ng-template #assignmentHostTemplate let-row>
  <app-assignment-host-label
    [serviceName]="row.workerServiceName"
    [hostName]="row.workerHostName"
    [workerId]="row.workerId"
    [fallback]="row.hostLabel"
  ></app-assignment-host-label>
</ng-template>

<ng-template #assignmentCountTemplate let-row>
  <app-assignment-result-cell
    [resultCount]="row.resultCount"
    [tooltip]="row.resultTooltip"
  ></app-assignment-result-cell>
</ng-template>

<ng-template #assignmentRssTemplate let-row>
  <app-assignment-rss-cell [rss]="row.rss"></app-assignment-rss-cell>
</ng-template>
